/**
 * Day-range helpers for the phone app's date filters.
 *
 * Distinct from monthRange.js, which bounds the workshop's customer browse to whole
 * calendar months. These bound to a rolling window of DAYS — "the last 7 days", or an
 * arbitrary pair a customer picked — which is what an invoice list on a phone needs.
 *
 * ⚠️ Everything here is LOCAL-time by construction. `toISOString()` is deliberately
 * never used: it converts to UTC first, so at 9am on the 19th in Melbourne (UTC+10)
 * it returns the 18th. A filter built that way silently drops a day's invoices, and
 * only for customers east of Greenwich — which is all of them.
 */

const pad = (n) => String(n).padStart(2, '0');

/** A Date → `YYYY-MM-DD` in the browser's own timezone. */
export const isoDay = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Today as `YYYY-MM-DD`, local time. */
export const today = () => isoDay(new Date());

/**
 * `YYYY-MM-DD` shifted by whole days.
 *
 * Parsed into local Y/M/D parts rather than `new Date(value)`, which reads a bare
 * `YYYY-MM-DD` as UTC midnight and lands on the previous day in any positive-offset
 * timezone. Day arithmetic on the Date constructor rolls month and year boundaries.
 */
export const shiftDay = (value, delta) => {
    const [y, m, d] = value.split('-').map(Number);
    return isoDay(new Date(y, m - 1, d + delta));
};

/**
 * The default window: today and the six days before it.
 *
 * Seven days INCLUSIVE of today, not today minus seven — the latter is an 8-day
 * window that reads as "a week" and is not one. Bounds are inclusive on both ends,
 * matching the server's BETWEEN.
 */
export const lastSevenDays = () => {
    const to = today();
    return { from: shiftDay(to, -6), to };
};

/**
 * Human label for a range, for the filter button.
 *
 * Deliberately shows the dates rather than "Last 7 days" once a custom range is in
 * play: on a filtered list the exact window is the thing the customer needs to see to
 * trust the totals in front of them.
 */
export const rangeLabel = ({ from, to }) => {
    if (!from && !to) return 'All time';
    if (from && !to) return `From ${shortDay(from)}`;
    if (!from && to) return `Until ${shortDay(to)}`;
    if (from === to) return shortDay(from);
    return `${shortDay(from)} – ${shortDay(to)}`;
};

/** `YYYY-MM-DD` → `19 Aug`, or the raw value if it is not a date. */
export const shortDay = (value) => {
    if (!value) return '';
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return value;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

/** True when the range is exactly the rolling 7-day default. */
export const isLastSevenDays = ({ from, to }) => {
    const preset = lastSevenDays();
    return from === preset.from && to === preset.to;
};
